import {OWWindow} from './ow-window';
import {OWUtils} from '../ow-utils';
import { OWWindowAnchorTypes } from './ow-window-anchor-types';

type GameInfoUpdatedEvent = overwolf.games.GameInfoUpdatedEvent;

// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Anchors a given window to an area of the game's screen - relevant only for
// in_game_only windows
export class OWGameWindowAnchor {
  private _window: OWWindow;
  private readonly _winWidth: number;
  private readonly _winHeight: number;
  private _anchorType: OWWindowAnchorTypes;
  private readonly _margin: number;

  // ---------------------------------------------------------------------------
  constructor(win: OWWindow,
              winClipArea: { width: number, height: number },
              anchorType: OWWindowAnchorTypes,
              margin = 10) {
    this._window = win;
    this._winWidth = winClipArea.width;
    this._winHeight = winClipArea.height;
    this._anchorType = anchorType;
    this._margin = margin;
  }

  // ---------------------------------------------------------------------------
  public start(): void {
    // Remove, potential previous listeners
    overwolf.games.onGameInfoUpdated.removeListener(this.onGameInfoUpdated);
    overwolf.games.onGameInfoUpdated.addListener(this.onGameInfoUpdated);

    overwolf.games.getRunningGameInfo(info => {
      if (!info) {
        return;
      }

      this.repositionWindow({
        width: info.logicalWidth,
        height: info.logicalHeight
      });
    });
  }

  // ---------------------------------------------------------------------------
  public stop(): void {
    overwolf.games.onGameInfoUpdated.removeListener(this.onGameInfoUpdated);
  }

  // ---------------------------------------------------------------------------
  private onGameInfoUpdated = (result: GameInfoUpdatedEvent): void => {
    if ((result == null) || (result.resolutionChanged == null)) {
      return;
    }

    if (result.resolutionChanged == false) {
      return;
    }

    if (result.gameInfo == null) {
      return;
    }

    const viewportWidth = result.gameInfo.logicalWidth;
    const viewportHeight = result.gameInfo.logicalHeight;
    this.repositionWindow({width: viewportWidth, height: viewportHeight});
  };

  // ---------------------------------------------------------------------------
  private async repositionWindow(viewport: { width: number, height: number })
    : Promise<void> {
    const scale = await OWUtils.getDPIScale();
    const margin = this._margin;

    let left = Math.floor(viewport.width - (this._winWidth * scale)) - margin;
    let top = margin;

    switch (this._anchorType) {
      case OWWindowAnchorTypes.topRight:
        break;
      case OWWindowAnchorTypes.middleRight:
        top = Math.floor(viewport.height / 2 - ((this._winHeight / 2) * scale));
        break;
      case OWWindowAnchorTypes.bottomRight:
        top = Math.floor(viewport.height - (this._winHeight * scale)) - margin;
        break;
      case OWWindowAnchorTypes.bottomLeft:
        left = margin;
        top = Math.floor(viewport.height - (this._winHeight * scale)) - margin;
        break;
      case OWWindowAnchorTypes.topLeft:
        left = margin;
        break;
    }

    await this._window.changePosition(left, top);
  }

  // ---------------------------------------------------------------------------
  public repositionAnchor(anchor) {
    this._anchorType = anchor;
    this.start();
  }
}
